import React from 'react';
import type { BoxProps, FlexProps } from '@withjoy/joykit';
import { Box, Flex, TextV2 } from '@withjoy/joykit';
import { pxToRem, styled } from '@withjoy/joykit/theme';

import { Home } from '@withjoy/joykit/icons';
import { useCardsRouterContext } from '@apps/card/Card.routes';
import { Link } from '@shared/core';
import { animationTransition } from '@shared/utils/animationTransition';
import { JoyLogo } from '@assets/index';
import { useIsMobileScreen } from '@shared/utils/media/useMediaScreens';
import { useNativeState } from '@shared/core/native';
import { useTranslation } from '@shared/core';
import { useDigitalCardBackdrop } from '@shared/components/DigitalCardBackdrop/useDigitalCardBackdrop';

// ==============================
// Root element
// ==============================

interface GridProps {
  limitedHeight?: boolean;
}

const Grid: React.FC<GridProps> = ({ children, limitedHeight = false }) => {
  return (
    <Flex
      minHeight="100vh"
      maxHeight={limitedHeight ? { md: '100vh' } : undefined}
      flexDirection={{ _: 'column', md: 'row' }}
    >
      {children}
    </Flex>
  );
};

// ==============================
// Primary Column
// ==============================

/** `backgroundColor` is a digital card's stored backdrop, so it may be a backdrop image's id. */
const Main: React.FC<{ backgroundVariant?: 'tinted'; backgroundColor?: string }> = ({ children, backgroundVariant, backgroundColor }) => {
  const bgColor = backgroundColor ? backgroundColor : backgroundVariant === 'tinted' ? 'mono1' : undefined;
  const backdrop = useDigitalCardBackdrop(bgColor);
  return (
    <Flex
      flexDirection="column"
      {...backdrop.surfaceProps}
      flex={{ md: 1 }}
      width="100%"
      transition={animationTransition('background-color', { duration: '300ms', timingFunction: 'ease-in-out' })}
    >
      {backdrop.layers}
      {children}
    </Flex>
  );
};

const Header: React.FC<{
  variant?: 'dashboard' | 'customizer';
  title?: string;
  borderVariant?: 'transparent';
  alignment?: BoxProps['justifyContent'];
  startIcon?: () => React.ReactNode;
  titleRedirectPath?: string;
}> = ({ alignment, children, title, startIcon, borderVariant, titleRedirectPath = '', variant = 'customizer' }) => {
  const { goToEventDashboard } = useCardsRouterContext();
  const isMobile = useIsMobileScreen();
  const { isNative } = useNativeState();
  const { tq } = useTranslation('stationery');

  return (
    <Flex
      height={variant === 'dashboard' ? pxToRem(80) : { _: 9, md: pxToRem(60) }}
      backgroundColor={'white'}
      {...(variant === 'dashboard' ? { paddingLeft: 6 } : {})}
      {...(variant === 'customizer' ? { paddingX: { _: 3, md: 6 } } : {})}
      paddingY={4}
      justifyContent={alignment || 'space-between'}
      alignItems="center"
      borderBottom="1px solid"
      borderBottomColor={borderVariant === 'transparent' ? 'transparent' : 'mono3'}
    >
      <Flex
        flexShrink={0}
        alignItems="center"
        cursor="pointer"
        columnGap={5}
        paddingRight={{ _: 3, md: 0 }}
        paddingY={{ _: 3, md: 0 }}
      >
        <Flex
          alignItems="center"
          gap={5}
        >
          {!isNative &&
            (startIcon?.() || (
              <Home
                size={24}
                cursor="pointer"
                onClick={goToEventDashboard}
              />
            ))}
          {variant === 'dashboard' && !isMobile && (
            <>
              <Flex>
                <JoyLogo
                  height={24}
                  width="100%"
                />
              </Flex>
              <TextV2
                as="span"
                color="mono14"
                fontSize={pxToRem(24)}
                fontWeight={600}
                lineHeight={1.5}
                letterSpacing={pxToRem(-0.24)}
              >
                {tq`Stationery`}
              </TextV2>
            </>
          )}
        </Flex>
        {variant === 'customizer' && (
          <Box
            width="1px"
            height="36px"
            backgroundColor="mono3"
          />
        )}
        {title && (
          <TextV2
            as={Link}
            to={titleRedirectPath}
            cursor="pointer"
            typographyVariant={{ _: 'hed2', md: 'hed5' }}
          >
            {title}
          </TextV2>
        )}
      </Flex>
      {children}
    </Flex>
  );
};

const Body: React.FC<Omit<BoxProps, '__css'>> = ({ children, ...restProps }) => {
  return (
    <Box
      overflowY="scroll"
      position="relative"
      padding={{ _: 6, md: 7 }}
      flex={1}
      {...restProps}
    >
      {children}
    </Box>
  );
};

// ==============================
// Secondary column
// ==============================

const SidePanelBlock: React.FC<{ fullBleed?: boolean } & BoxProps> = ({ backgroundColor, children, fullBleed, paddingY, rowGap = 0, ...boxProps }) => {
  return (
    <Flex
      flexDirection="column"
      backgroundColor={backgroundColor}
      paddingY={paddingY}
      paddingX={fullBleed ? undefined : 7}
      rowGap={rowGap}
      {...boxProps}
    >
      {children}
    </Flex>
  );
};

const SidePanelRoot = styled(Flex)``;

export interface SidePanelProps {
  title: string;
  children: React.ReactNode;
  rowGap?: number;
  withBlur?: boolean;
  paddingBottom?: number;
  overflowY?: 'hidden' | 'scroll';
}

const SidePanel = React.forwardRef(
  ({ children, title, rowGap = 7, withBlur = false, paddingBottom = 81, overflowY = 'scroll', ...props }: SidePanelProps & FlexProps, ref: React.Ref<HTMLDivElement>) => {
    return (
      <SidePanelRoot
        ref={ref}
        flex={{ md: '0 0 375px' }}
        width={{ md: 375 }}
        flexDirection="column"
        rowGap={rowGap}
        paddingTop={7}
        right={{ md: 0 }}
        top={{ md: 0 }}
        borderLeft="1px solid"
        borderLeftColor="mono3"
        height={{ md: '100vh' }}
        paddingBottom={{ _: paddingBottom }}
        filter={withBlur ? 'blur(3px)' : 'none'}
        position="relative"
        {...props}
        overflowY={{ md: overflowY }}
      >
        <TextV2
          typographyVariant="hed6"
          fontWeight={600}
          paddingX={7}
        >
          {title}
        </TextV2>
        {children}
      </SidePanelRoot>
    );
  },
);

export const Layout = {
  Grid,

  Main,
  Body,
  Header,

  SidePanel,
  SidePanelBlock,
};
